import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Modal, ModalActions } from '../../../shared/ui/Modal';
import { Button } from '../../../shared/ui/Button';
import { Checkbox } from '../../../shared/ui/Checkbox';
import { Skeleton } from '../../../shared/ui/Skeleton';
import { Tooltip } from '../../../shared/ui/Tooltip';
import { toast } from '../../../shared/ui/toast';
import { useDeletedMailboxes, useQuota, useRestoreMailboxes } from '../api';
import { useRetention } from '../useRetention';

export function RestoreMailboxesModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const deleted = useDeletedMailboxes(open);
  const quota = useQuota();
  const retention = useRetention();
  const restore = useRestoreMailboxes();
  const items = useMemo(() => deleted.data?.items ?? [], [deleted.data]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  // Сколько ящиков можно восстановить (лимит активных минус уже занятые слоты).
  const restoreLimit = quota.data
    ? Math.max(0, quota.data.active.limit - quota.data.active.used)
    : items.length;

  // Один адрес может занять только один активный ящик → в рамках дубликатов выбираем максимум один.
  const selectedAddresses = useMemo(() => {
    const set = new Set<string>();
    for (const m of items) if (selected.has(m.id)) set.add(m.address.full);
    return set;
  }, [items, selected]);

  const atLimit = selected.size >= restoreLimit;

  const isDisabled = (id: string, address: string): boolean => {
    if (selected.has(id)) return false;
    if (atLimit) return true;
    // дубликат: другой ящик с тем же адресом уже выбран
    return selectedAddresses.has(address);
  };

  const toggle = (id: string, v: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (v) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (v: boolean) => {
    if (!v) {
      setSelected(new Set());
      return;
    }
    // Жадно выбираем по одному на адрес, пока не упрёмся в лимит.
    const next = new Set<string>();
    const usedAddresses = new Set<string>();
    for (const m of items) {
      if (next.size >= restoreLimit) break;
      if (usedAddresses.has(m.address.full)) continue;
      next.add(m.id);
      usedAddresses.add(m.address.full);
    }
    setSelected(next);
  };

  const selectableCount = useMemo(() => {
    const uniqueAddresses = new Set(items.map((m) => m.address.full));
    return Math.min(restoreLimit, uniqueAddresses.size);
  }, [items, restoreLimit]);

  const activeLimit = quota.data?.active.limit ?? restoreLimit;
  const freeSlots = Math.max(0, restoreLimit - selected.size);

  const allChecked = selectableCount > 0 && selected.size >= selectableCount;
  const someChecked = selected.size > 0;

  const onConfirm = () => {
    restore.mutate([...selected], {
      onSuccess: (res) => {
        if (res.failed.length === 0) {
          toast.success(t('mailbox.toasts.restored'));
          onOpenChange(false);
        } else if (res.ok.length > 0) {
          toast.warning(
            t('mailbox.toasts.restorePartial', {
              ok: res.ok.length,
              total: res.ok.length + res.failed.length,
            })
          );
          setSelected(new Set());
        } else {
          toast.error(t('mailbox.toasts.restoreError'), t('mailbox.create.errors.already_exists'));
        }
      },
      onError: () => {
        toast.error(t('mailbox.toasts.restoreError'));
      },
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('mailbox.restore.title')}
      description={t('mailbox.restore.subtitle', { count: retention.mailboxTrashDays })}
    >
      {deleted.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-sm text-fg-muted">
            {t('mailbox.restore.limitInfo', { count: freeSlots, limit: activeLimit })}
            <Tooltip content={t('mailbox.restore.limitTooltip')}>
              <button type="button" className="text-accent" aria-label="info">
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </p>

          {items.length > 0 && (
            <label className="flex items-center gap-3 text-sm font-medium text-fg">
              <Checkbox
                checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                aria-label={t('common.selectAll')}
              />
              {t('common.selectAll')}
            </label>
          )}

          <div className="scroll-slim max-h-72 space-y-1 overflow-y-auto overflow-x-hidden pr-1">
            {items.map((m) => {
              const disabled = isDisabled(m.id, m.address.full);
              return (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 rounded-lg px-1 py-1.5 ${
                    disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-page/60'
                  }`}
                  title={disabled && !atLimit ? t('mailbox.restore.duplicateDisabled') : undefined}
                >
                  <Checkbox
                    checked={selected.has(m.id)}
                    onCheckedChange={(v) => {
                      toggle(m.id, v);
                    }}
                    aria-label={m.address.full}
                    disabled={disabled}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-fg">{m.address.full}</span>
                    {m.description && (
                      <span className="block truncate text-xs text-fg-muted">{m.description}</span>
                    )}
                  </span>
                </label>
              );
            })}
            {items.length === 0 && <p className="text-sm text-fg-muted">{t('mailbox.restore.empty')}</p>}
          </div>
        </div>
      )}

      <ModalActions>
        <Button onClick={onConfirm} loading={restore.isPending} disabled={selected.size === 0}>
          {t('mailbox.restore.submit')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          {t('common.cancel')}
        </Button>
      </ModalActions>
    </Modal>
  );
}
